// Location: src/utils/ga4.ts
// Google Analytics 4 (GA4) utility for user tracking and event tracking
// This file provides methods to identify users and track custom events in GA4
// RELEVANT FILES: src/stores/auth.ts, src/services/aws-auth.service.ts, index.html

/**
 * Type definitions for Google Analytics gtag function
 */
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set' | 'get',
      targetId: string | object,
      params?: Record<string, any>,
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Check if we're on production domain
 * IMPORTANT: GA4 tracking is ONLY enabled on portal.retaintive.ai
 */
function isProductionDomain(): boolean {
  return typeof window !== 'undefined' && window.location.hostname === 'portal.retaintive.ai';
}

/**
 * Check if GA4 is loaded and available
 * Only returns true on production domain (portal.retaintive.ai)
 *
 * This ensures GA4 tracking is completely disabled in:
 * - Development (localhost)
 * - Test environment (portal-test.retaintive.ai)
 * - Pre-production (portal-pre.retaintive.ai)
 */
export function isGALoaded(): boolean {
  const isProduction = isProductionDomain();
  const hasGtag = typeof window !== 'undefined' && typeof window.gtag === 'function';

  return isProduction && hasGtag;
}

/**
 * Set user ID in GA4 using Cognito sub
 * This allows tracking of individual user behavior across sessions
 *
 * @param userId - The Cognito sub (unique user identifier)
 *
 * @example
 * // After user logs in:
 * setGAUserId(user.cognitoSub)
 */
export function setGAUserId(userId: string | null): void {
  if (!isGALoaded()) {
    return;
  }

  if (!userId) {
    return;
  }

  try {
    // Set user_id in GA4
    window.gtag?.('config', 'G-R8G4C7HGVF', {
      user_id: userId,
    });
  } catch (error) {
    console.error('Failed to set GA4 user ID:', error);
  }
}

/**
 * Clear user ID from GA4 (call on logout)
 */
export function clearGAUserId(): void {
  if (!isGALoaded()) {
    return;
  }

  try {
    window.gtag?.('config', 'G-R8G4C7HGVF', {
      user_id: null,
    });
  } catch (error) {
    console.error('Failed to clear GA4 user ID:', error);
  }
}

/**
 * Track custom events in GA4
 *
 * @param eventName - Name of the event
 * @param eventParams - Additional parameters for the event
 *
 * @example
 * trackGAEvent('button_click', {
 *   button_name: 'create_task',
 *   page: '/dashboard'
 * })
 */
export function trackGAEvent(eventName: string, eventParams?: Record<string, any>): void {
  if (!isGALoaded()) {
    return;
  }

  try {
    window.gtag?.('event', eventName, eventParams);
  } catch (error) {
    console.error('Failed to track GA4 event:', error);
  }
}

/**
 * Set user properties in GA4
 *
 * @param properties - User properties to set
 *
 * @example
 * setGAUserProperties({
 *   org_count: 3,
 *   user_role: 'admin'
 * })
 */
export function setGAUserProperties(properties: Record<string, any>): void {
  if (!isGALoaded()) {
    return;
  }

  try {
    window.gtag?.('set', 'user_properties', properties);
  } catch (error) {
    console.error('Failed to set GA4 user properties:', error);
  }
}

// ============================================
// ONBOARDING & ACTIVATION EVENTS
// ============================================

/**
 * Track user sign up event
 */
export function trackSignUp(method: string = 'email'): void {
  trackGAEvent('sign_up', { method });
}

/**
 * Track organization creation
 */
export function trackOrganizationCreated(orgId: string, orgName?: string): void {
  trackGAEvent('organization_created', {
    org_id: orgId,
    org_name: orgName,
  });
}

/**
 * Track RingCentral integration connection (key activation metric)
 */
export function trackIntegrationConnected(orgId: string): void {
  trackGAEvent('integration_connected', {
    integration_type: 'ringcentral',
    org_id: orgId,
  });
}

/**
 * Track team member invitation
 */
export function trackTeamMemberInvited(orgId: string, invitedRole: string): void {
  trackGAEvent('team_member_invited', {
    org_id: orgId,
    invited_role: invitedRole,
  });
}

// ============================================
// CORE FEATURE ENGAGEMENT EVENTS
// ============================================

/**
 * Track call search usage
 */
export function trackCallSearch(searchTerm: string, resultsCount: number, orgId?: string): void {
  trackGAEvent('call_search', {
    search_term: searchTerm,
    results_count: resultsCount,
    org_id: orgId,
  });
}

/**
 * Track AI analysis view (separate from basic call view)
 */
export function trackCallAnalysisViewed(
  callId: string,
  orgId: string,
  additionalParams?: Record<string, any>,
): void {
  trackGAEvent('call_analysis_viewed', {
    call_id: callId,
    org_id: orgId,
    ...additionalParams,
  });
}

/**
 * Track transcript scrolling depth
 */
export function trackCallTranscriptScrolled(callId: string, scrollDepth: 25 | 50 | 75 | 100): void {
  trackGAEvent('call_transcript_scrolled', {
    call_id: callId,
    scroll_depth: scrollDepth,
  });
}

/**
 * Track dashboard widget interactions
 */
export function trackDashboardWidgetInteraction(
  widgetName: string,
  interactionType: string,
  orgId?: string,
): void {
  trackGAEvent('dashboard_widget_interaction', {
    widget_name: widgetName,
    interaction_type: interactionType,
    org_id: orgId,
  });
}

// ============================================
// CONVERSION EVENTS
// ============================================

/**
 * Track first call analysis view (micro-conversion)
 */
export function trackFirstCallAnalysisViewed(orgId: string, callId: string): void {
  trackGAEvent('first_call_analysis_viewed', {
    org_id: orgId,
    call_id: callId,
  });
}

/**
 * Track subscription started (primary conversion)
 */
export function trackSubscriptionStarted(planType: string, orgId?: string): void {
  trackGAEvent('subscription_started', {
    plan_type: planType,
    org_id: orgId,
  });
}

/**
 * Track contact sales action (primary conversion for enterprise)
 */
export function trackContactSales(orgId?: string, source?: string): void {
  trackGAEvent('contact_sales', {
    org_id: orgId,
    source: source,
  });
}

// ============================================
// PERFORMANCE TRACKING
// ============================================

/**
 * Track performance metrics
 */
export function trackPerformanceMetric(
  metricName: 'page_load' | 'api_response' | string,
  metricValue: number,
  pagePath?: string,
  apiEndpoint?: string,
): void {
  trackGAEvent('performance_metric', {
    metric_name: metricName,
    metric_value: metricValue,
    page_path: pagePath || window.location.pathname,
    api_endpoint: apiEndpoint,
  });
}

// ============================================
// ENHANCED USER PROPERTIES
// ============================================

/**
 * Set comprehensive user properties for segmentation
 */
export function setComprehensiveUserProperties(properties: {
  org_count?: number;
  user_role?: string;
  is_multi_org_user?: boolean;
  plan_type?: string;
  account_age_days?: number;
  team_size?: number;
  total_calls_analyzed?: number;
  last_login_date?: string;
}): void {
  setGAUserProperties(properties);
}
